import { hasValue } from "@arti/utilities";
import { AnimatePresence, motion } from "framer-motion";
import { type FC, useMemo, useState } from "react";

import type {
  RepositoryIndexingInsight,
  RepositoryIndexingInsightFacet,
  RepositoryIndexingRun,
} from "../../types";

import { formatTimestamp } from "../../utils/sourceControlIndexing";
import { Badge, type BadgeProps } from "../common/Badge";
import { DataState } from "../common/DataState";
import { FloatingCard } from "../common/FloatingCard";
import { FormInput, FormSelect } from "../common/forms";
import { JsonViewer } from "../common/JsonViewer";
import { ListItemCard, ListItemCardHeader } from "../common/ListItemCard";
import { LoadingSkeleton } from "../common/LoadingState";
import { Modal } from "../common/Modal";
import { PanelHeaderBlock } from "../common/PanelHeaderBlock";
import { PanelSection } from "../common/PanelSection";
import { StatusBadge } from "../common/StatusBadge";
import { Text } from "../common/Text";
import { RepositoryIdentityInsightDetails } from "./RepositoryIdentityInsightDetails";
import { RepositoryIdentityInsightSummary } from "./RepositoryIdentityInsightSummary";

interface Props {
  selectedRun: RepositoryIndexingRun | null;
  insights: RepositoryIndexingInsight[];
  loading: boolean;
  error: string | null;
  title?: string;
  description?: string;
  eyebrow?: string;
}

const mapSeverityToVariant = (
  severity: string | null,
): BadgeProps["variant"] => {
  switch (severity?.toLowerCase()) {
    case "high": {
      return "danger";
    }
    case "medium": {
      return "warning";
    }
    case "low": {
      return "info";
    }
    default: {
      return "default";
    }
  }
};

function formatInsightType(type: string): string {
  return type
    .split("_")
    .filter((part) => part)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function insightLayoutId(insightId: string): string {
  return `repo-indexing-insight-${insightId}`;
}

const RunCompletionDetail: FC<{ run: RepositoryIndexingRun }> = ({ run }) => {
  if (run.status === "completed") {
    return <span> · Completed {formatTimestamp(run.completed_at)}</span>;
  }
  if (run.status === "failed") {
    return (
      <span>
        {" "}
        · Failed
        {run.completed_at ? ` ${formatTimestamp(run.completed_at)}` : ""}
      </span>
    );
  }
  return <span> · In progress</span>;
};

// --- Subcomponents ---

const InsightHeader: FC<{
  name: string | null;
  type: string;
  version: string | null;
  severity: string | null;
  repositorySlug: string;
  sourceFile: string | null;
  description: string | null;
}> = ({
  name,
  type,
  version,
  severity,
  repositorySlug,
  sourceFile,
  description,
}) => (
  <div className="flex flex-wrap items-start justify-between gap-2">
    <div className="min-w-0 flex-1 space-y-1">
      <div className="flex items-center gap-2">
        <p className="truncate font-mono text-[11px] font-bold uppercase tracking-widest text-gray-900 dark:text-white">
          {name ?? type}
        </p>
        {version && (
          <Text
            variant="label"
            className="rounded-none border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-gray-600 dark:border-slate-700 dark:bg-arti-background dark:text-gray-300"
          >
            v{version}
          </Text>
        )}
      </div>

      <Text as="p" className="flex flex-wrap items-center gap-1">
        <span>{repositorySlug}</span>
        {sourceFile && (
          <>
            <span className="hidden sm:inline">•</span>
            <span
              className="max-w-[300px] truncate font-mono text-xs"
              title={sourceFile}
            >
              {sourceFile}
            </span>
          </>
        )}
      </Text>

      {description && (
        <p className="line-clamp-2 max-w-3xl text-xs text-gray-600 dark:text-gray-400">
          {description}
        </p>
      )}
    </div>

    {severity && (
      <Badge
        variant={mapSeverityToVariant(severity)}
        size="sm"
        className="flex-shrink-0 uppercase"
        aria-label={`Severity ${severity}`}
      >
        {severity}
      </Badge>
    )}
  </div>
);

const InsightMetrics: FC<{
  coverage: number | null;
  confidence: number | null;
  count: number | null;
}> = ({ coverage, confidence, count }) => {
  if (coverage === null && confidence === null && count === null) {
    return null;
  }

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {coverage !== null && (
        <Badge variant="default" size="sm" aria-label={`Coverage ${coverage}%`}>
          Coverage: {coverage}%
        </Badge>
      )}
      {confidence !== null && (
        <Badge
          variant="default"
          size="sm"
          aria-label={`Confidence score ${confidence}`}
        >
          Confidence: {confidence}
        </Badge>
      )}
      {count !== null && (
        <Badge variant="default" size="sm" aria-label={`Count ${count}`}>
          Count: {count}
        </Badge>
      )}
    </div>
  );
};

const InsightMeta: FC<{
  primaryLanguage: string | null;
  category: string | null;
  architecturePattern: string | null;
  detectionMethod: string | null;
}> = ({ primaryLanguage, category, architecturePattern, detectionMethod }) => {
  const hasTags =
    hasValue(primaryLanguage) ||
    hasValue(category) ||
    hasValue(architecturePattern);

  return (
    <>
      {hasTags && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {primaryLanguage && (
            <Badge variant="default" size="sm">
              Language: {primaryLanguage}
            </Badge>
          )}
          {category && (
            <Badge variant="default" size="sm">
              Category: {category}
            </Badge>
          )}
          {architecturePattern && (
            <Badge variant="default" size="sm">
              Pattern: {architecturePattern}
            </Badge>
          )}
        </div>
      )}

      {detectionMethod && (
        <p className="mt-2 text-xs text-gray-400">
          Detected via {detectionMethod}
        </p>
      )}
    </>
  );
};

const InsightFacets: FC<{ facets: RepositoryIndexingInsightFacet[] }> = ({
  facets,
}) => {
  if (facets.length === 0) {
    return null;
  }
  const grouped = new Map<string, string[]>();
  for (const facet of facets) {
    const key = facet.facet_type.trim();
    if (!key) {
      continue;
    }
    const list = grouped.get(key) ?? [];
    if (!list.includes(facet.facet_value)) {
      list.push(facet.facet_value);
      grouped.set(key, list);
    }
  }

  const entries = Array.from(grouped.entries()).toSorted(([a], [b]) =>
    a.localeCompare(b),
  );
  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 border-t border-gray-100 pt-3 dark:border-white/5">
      <div className="space-y-2">
        {entries.map(([facetType, values]) => (
          <div
            key={facetType}
            className="rounded-none border border-slate-300 bg-white/50 px-2.5 py-2 dark:border-slate-700 dark:bg-arti-panel"
          >
            <Text
              as="p"
              variant="label"
              className="text-gray-700 dark:text-white/80"
            >
              {formatInsightType(facetType)}
            </Text>
            <p className="mt-1 text-xs text-gray-600 dark:text-white/65">
              {values.join(" • ")}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const CollapsibleMetadata: FC<{ data: Record<string, unknown> }> = ({
  data,
}) => {
  if (Object.keys(data).length === 0) {
    return null;
  }

  return (
    <div className="mt-3 border-t border-gray-100 pt-2 dark:border-white/5">
      <details className="group">
        <summary className="cursor-pointer select-none text-xs font-medium text-gray-500 outline-none hover:text-gray-700 focus:text-gray-700 dark:text-white/50 dark:hover:text-white/70">
          <span className="group-open:hidden">Show Metadata</span>
          <span className="hidden group-open:inline">Hide Metadata</span>
        </summary>
        <div className="mt-2">
          <JsonViewer
            data={data}
            defaultMode="pretty"
            maxHeight="max-h-64"
            className="font-mono"
          />
        </div>
      </details>
    </div>
  );
};

const InsightCard: FC<{
  insight: RepositoryIndexingInsight;
  onOpenIdentityProfile?: (insight: RepositoryIndexingInsight) => void;
}> = ({ onOpenIdentityProfile, insight }) => {
  const isRepositoryIdentity = insight.insight_type === "repository_identity";

  return (
    <motion.div
      layoutId={
        isRepositoryIdentity ? insightLayoutId(insight.insight_id) : undefined
      }
      transition={{ layout: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } }}
    >
      <FloatingCard
        padding="lg"
        className="border-slate-300 !bg-white dark:border-slate-700 dark:!bg-arti-panel"
      >
        <InsightHeader
          name={insight.name}
          type={insight.insight_type}
          version={insight.version}
          severity={insight.severity}
          repositorySlug={insight.repository_slug}
          sourceFile={insight.source_file}
          description={insight.description}
        />

        <InsightMeta
          primaryLanguage={insight.primary_language}
          category={insight.category}
          architecturePattern={insight.architecture_pattern}
          detectionMethod={insight.detection_method}
        />

        <InsightMetrics
          coverage={insight.coverage}
          confidence={insight.confidence_score}
          count={insight.count}
        />

        {isRepositoryIdentity ? (
          <RepositoryIdentityInsightSummary
            insight={insight}
            onOpenProfile={() => onOpenIdentityProfile?.(insight)}
          />
        ) : (
          <InsightFacets facets={insight.facets} />
        )}

        {!isRepositoryIdentity && (
          <CollapsibleMetadata data={insight.metadata ?? {}} />
        )}
      </FloatingCard>
    </motion.div>
  );
};

export const RepositoryIndexingInsightsPanel: FC<Props> = ({
  selectedRun,
  insights,
  loading,
  error,
  title = "Repository Insights",
  description = "Inspect and filter insights captured during the selected run.",
  eyebrow = "Step 2",
}) => {
  const [searchText, setSearchText] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [expandedIdentityInsightId, setExpandedIdentityInsightId] = useState<
    string | null
  >(null);

  const insightTypeOptions = useMemo(() => {
    const options = Array.from(
      new Set(insights.map((insight) => insight.insight_type)),
    )
      .toSorted((a, b) => a.localeCompare(b))
      .map((type) => ({ label: formatInsightType(type), value: type }));

    return [{ label: "All types", value: "all" }, ...options];
  }, [insights]);

  const activeType = insightTypeOptions.some(
    (option) => option.value === selectedType,
  )
    ? selectedType
    : "all";

  const normalizedQuery = searchText.trim().toLowerCase();

  const filteredInsights = useMemo(
    () =>
      insights.filter((insight) => {
        if (activeType !== "all" && insight.insight_type !== activeType) {
          return false;
        }

        if (!normalizedQuery) {
          return true;
        }

        const baseText = [
          insight.name,
          insight.description,
          insight.repository_slug,
          insight.insight_type,
          insight.primary_language,
          insight.category,
          insight.architecture_pattern,
          insight.source_file,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (baseText.includes(normalizedQuery)) {
          return true;
        }

        return insight.facets.some((facet) =>
          `${facet.facet_type} ${facet.facet_value}`
            .toLowerCase()
            .includes(normalizedQuery),
        );
      }),
    [activeType, insights, normalizedQuery],
  );

  const isFiltered = normalizedQuery.length > 0 || activeType !== "all";
  const expandedInsight =
    insights.find(
      (insight) => insight.insight_id === expandedIdentityInsightId,
    ) ?? null;

  const headerContent = (
    <PanelHeaderBlock
      eyebrow={eyebrow}
      title={title}
      description={description}
    />
  );

  return (
    <PanelSection headerContent={headerContent}>
      {!selectedRun ? (
        <ListItemCard padding="lg" className="border-dashed">
          <p className="text-sm text-gray-500 dark:text-white/60">
            Select a run from the left panel to view and filter its insights.
          </p>
        </ListItemCard>
      ) : (
        <div className="space-y-4">
          <ListItemCard
            padding="md"
            className="border-slate-300 bg-slate-50/70 dark:border-slate-700 dark:bg-arti-panel"
            header={
              <ListItemCardHeader
                title={selectedRun.run_type}
                subtitle={
                  <>
                    Started {formatTimestamp(selectedRun.started_at)}
                    <RunCompletionDetail run={selectedRun} />
                  </>
                }
              />
            }
            actions={<StatusBadge status={selectedRun.status} />}
          />

          <div className="grid gap-2 md:grid-cols-[1fr,220px]">
            <FormInput
              size="sm"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Search insights, repositories, facets..."
              aria-label="Search insights"
            />
            <FormSelect
              size="sm"
              value={activeType}
              onChange={(event) => setSelectedType(event.target.value)}
              options={insightTypeOptions}
              aria-label="Filter by insight type"
            />
          </div>

          <Text as="p" variant="label">
            Showing {filteredInsights.length} of {insights.length} insights
            {isFiltered ? " (filtered)" : ""}.
          </Text>

          <DataState
            loading={loading}
            error={error}
            empty={filteredInsights.length === 0 && !loading}
            emptyTitle={
              isFiltered ? "No matching insights" : "No insights found"
            }
            emptyDescription={
              isFiltered
                ? "Adjust search text or insight type filters."
                : "This run has not produced any insights yet."
            }
            loadingComponent={<LoadingSkeleton rows={3} className="mt-4" />}
          >
            <motion.div
              layoutScroll
              className="custom-scrollbar max-h-[48rem] space-y-4 overflow-y-auto pr-2"
            >
              {filteredInsights.map((insight) => (
                <InsightCard
                  key={insight.insight_id}
                  insight={insight}
                  onOpenIdentityProfile={(candidate) =>
                    setExpandedIdentityInsightId(candidate.insight_id)
                  }
                />
              ))}
            </motion.div>
          </DataState>
        </div>
      )}

      <AnimatePresence>
        {expandedInsight && (
          <Modal
            title={expandedInsight.name ?? "Repository Profile"}
            subtitle={expandedInsight.repository_slug}
            meta={`Insight ${expandedInsight.insight_id}`}
            onClose={() => setExpandedIdentityInsightId(null)}
            widthClass="max-w-6xl"
            useMotion
            overlayMotionProps={{
              animate: { opacity: 1 },
              exit: { opacity: 0 },
              initial: { opacity: 0 },
              transition: { duration: 0.2 },
            }}
            panelMotionProps={{
              layoutId: insightLayoutId(expandedInsight.insight_id),
              transition: { layout: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } },
            }}
          >
            <div className="space-y-3">
              <RepositoryIdentityInsightDetails insight={expandedInsight} />
              <CollapsibleMetadata data={expandedInsight.metadata ?? {}} />
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </PanelSection>
  );
};
